import { useCallback, useEffect, useState } from "react";
import { Loader2, Link2, RefreshCw, AlertTriangle, CheckCircle2, CircleSlash, Info } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const STROKE = 1.5;

const CATEGORY_LABEL = {
  experience: "Experience",
  education: "Education",
  skills: "Skill",
  certification: "Certification",
  project: "Project",
  other: "Claim",
};

// Phase 4f.1 — CV ↔ interview factual cross-reference (Option B).
// Purely factual: lists each CV claim and where (if anywhere) it came up
// verbally. Never judges accuracy or recommends a decision. The verbal-only
// audit chat is untouched.
export default function CvCrossref({ candidateId, canGenerate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/candidates/${candidateId}/cv/crossref`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/candidates/${candidateId}/cv/crossref`);
      setData(res.data);
      if (res.data.exists) toast.success("Cross-reference ready.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cross-reference failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-ink-400" strokeWidth={STROKE} /></div>;
  }
  if (!data || !data.has_cv) return null;

  if (!data.cv_searchable) {
    return (
      <div className="rounded-md border border-ink-200 bg-ink-50 px-3 py-2 text-[12px] text-ink-600" data-testid="crossref-not-searchable">
        This file isn&rsquo;t searchable (no extractable text), so it can&rsquo;t be cross-referenced with the interviews.
      </div>
    );
  }
  if (!data.has_transcripts) {
    return (
      <div className="rounded-md border border-ink-200 bg-ink-50 px-3 py-2 text-[12px] text-ink-600" data-testid="crossref-no-transcripts">
        Add and transcribe an interview to cross-reference this CV with what was said.
      </div>
    );
  }

  const items = data.items || [];

  return (
    <div data-testid="cv-crossref">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[13px] font-medium text-ink-800">
          <Link2 className="h-4 w-4 text-emerald-800" strokeWidth={STROKE} />
          CV vs. what was said
        </div>
        {data.exists && canGenerate && (
          <button
            onClick={generate}
            disabled={generating}
            className="inline-flex items-center gap-1.5 rounded-md border border-ink-200 bg-white px-2.5 py-1 text-[12px] font-medium text-ink-600 transition hover:border-emerald-800/40 hover:text-emerald-900 disabled:opacity-60"
            data-testid="crossref-regenerate"
          >
            {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={STROKE} /> : <RefreshCw className="h-3.5 w-3.5" strokeWidth={STROKE} />}
            {generating ? "Working…" : "Refresh"}
          </button>
        )}
      </div>

      {!data.exists ? (
        <div className="mt-2 rounded-md border border-ink-200 bg-white p-4" data-testid="crossref-empty">
          <p className="text-[13px] text-ink-600">
            Lay the CV&rsquo;s claims next to the interviews — see, factually, where each one came up verbally.
            This never judges accuracy or suggests a hiring decision.
          </p>
          {canGenerate ? (
            <button
              onClick={generate}
              disabled={generating}
              className="mt-3 inline-flex items-center gap-2 rounded-md bg-emerald-800 px-3 py-1.5 text-[13px] font-medium text-white transition hover:bg-emerald-900 disabled:opacity-60"
              data-testid="crossref-generate"
            >
              {generating ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} /> : <Link2 className="h-4 w-4" strokeWidth={STROKE} />}
              {generating ? "Cross-referencing…" : "Cross-reference with interviews"}
            </button>
          ) : (
            <p className="mt-2 text-[12px] text-ink-400">A recruiter can generate this cross-reference.</p>
          )}
        </div>
      ) : (
        <>
          {data.is_stale && (
            <div className="mt-2 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-[12px] text-amber-900" data-testid="crossref-stale">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" strokeWidth={STROKE} />
              The CV or interviews changed since this was generated.{canGenerate ? " Refresh to update." : ""}
            </div>
          )}
          <div className="mt-2 flex items-start gap-2 rounded-md border border-emerald-800/15 bg-emerald-50/40 px-3 py-2 text-[12px] text-emerald-900" data-testid="crossref-compliance">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={STROKE} />
            <span>
              Factual only: this lists what the CV states and where each item came up verbally.
              &ldquo;Not discussed&rdquo; is common and is <strong>not</strong> a negative signal — it doesn&rsquo;t judge accuracy or recommend a decision.
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-ink-500" data-testid="crossref-counts">
            <span className="inline-flex items-center gap-1 rounded-md border border-emerald-800/20 bg-white px-2 py-0.5">
              <CheckCircle2 className="h-3 w-3 text-emerald-700" strokeWidth={STROKE} /> {data.discussed_count} discussed verbally
            </span>
            <span className="inline-flex items-center gap-1 rounded-md border border-ink-200 bg-white px-2 py-0.5">
              <CircleSlash className="h-3 w-3 text-ink-400" strokeWidth={STROKE} /> {data.not_discussed_count} not discussed
            </span>
          </div>

          <ul className="mt-3 space-y-2">
            {items.map((it, idx) => (
              <li key={idx} className="rounded-md border border-ink-100 bg-white p-3" data-testid={`crossref-item-${idx}`}>
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[13px] font-medium text-ink-900">{it.claim}</p>
                  <span className="shrink-0 rounded border border-ink-200 bg-ink-50 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-ink-500">
                    {CATEGORY_LABEL[it.category] || "Claim"}
                  </span>
                </div>
                {it.discussed ? (
                  <div className="mt-2 space-y-1.5">
                    {it.evidence.map((ev, j) => (
                      <div key={j} className="border-l-2 border-emerald-800/40 pl-2.5" data-testid={`crossref-evidence-${idx}-${j}`}>
                        <div className="text-[11px] font-medium text-emerald-800">
                          Round {ev.round}{ev.timestamp ? ` · ~${ev.timestamp}` : ""}
                        </div>
                        <p className="text-[12px] italic leading-relaxed text-ink-600">&ldquo;{ev.quote}&rdquo;</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-ink-400" data-testid={`crossref-notdiscussed-${idx}`}>
                    <CircleSlash className="h-3 w-3" strokeWidth={STROKE} /> Not discussed in the interviews
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
